import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';

import { principalConfirm } from 'api/Wizard/Wizard';

import { cn } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import AccordionConfirmModals from './components/AccordionConfirmModals';
import AccordionTrigger from './components/AccordionTrigger';
import DefaultReviewComponent from './components/DefaultReviewComponent';

const Accordion = ({
  stepTitle,
  noPanel = false,
  panelComponent,
  ButtonComponent = null,
  panelClassName,
  className,
  navigateToSubstep,
  wizardInstanceId,
  data,
  invalidateQueryKeys = ['wizard-overview'],
  defaultOpen = false,
  practiceId
}) => {
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [unconfirmationModalVisible, setUnconfirmationModalVisible] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const navigate = useNavigate();
  const isStepCompleted = data?.status === 'Completed' || data?.status === 'Confirmed';
  const isStepConfirmed = data?.status === 'Confirmed';

  const completedSubsteps = data?.substeps?.filter(
    (substep) => substep.status === 'Completed' || substep.status === 'Confirmed'
  )?.length;
  const completedSubstepsPercentage = data?.substeps
    ? Math.round((completedSubsteps / data.substeps.length) * 100)
    : 0;

  const { mutateAsync: principalConfirmation } = useCustomMutation({
    mutationFn: (payload) => {
      return principalConfirm(navigate, {
        wizard_instance_id: wizardInstanceId,
        substep_id: payload.substep_id,
        step_id: payload.step_id,
        action: payload.action,
        practiceId
      });
    },
    invalidateQueryKey: invalidateQueryKeys
  });

  const handleConfirmClick = (e) => {
    e.stopPropagation();
    setConfirmationModalVisible(true);
  };

  const handleConfirmContinue = async () => {
    setIsConfirming(true);
    try {
      await principalConfirmation({
        wizard_instance_id: wizardInstanceId,
        step_id: data.step_id,
        action: 'confirm'
      });
      setConfirmationModalVisible(false);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleUnconfirmClick = () => {
    setUnconfirmationModalVisible(true);
  };

  const handleUnconfirmContinue = async () => {
    setIsConfirming(true);
    try {
      await principalConfirmation({
        wizard_instance_id: wizardInstanceId,
        step_id: data.step_id,
        action: 'unconfirm'
      });
      setUnconfirmationModalVisible(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <>
      <Disclosure
        as="div"
        defaultOpen={defaultOpen}
        className={cn(
          'overflow-hidden rounded-lg border border-solid border-neutral-200',
          className
        )}>
        {({ open }) => (
          <>
            <DisclosureButton
              className={`flex w-full items-center justify-between sm:flex-col sm:items-start ${
                open ? 'border-b border-solid border-neutral-200 last:border-b-0' : ''
              } px-2 py-1`}>
              <AccordionTrigger
                open={open}
                ButtonComponent={ButtonComponent}
                stepTitle={stepTitle}
                isStepCompleted={isStepCompleted}
                isStepConfirmed={isStepConfirmed}
                completedSubsteps={completedSubsteps}
                totalSubsteps={data?.substeps?.length}
                completedSubstepsPercentage={completedSubstepsPercentage}
                onConfirmClick={handleConfirmClick}
                onUnconfirmClick={handleUnconfirmClick}
              />
            </DisclosureButton>
            {!noPanel && (
              <DisclosurePanel className={cn('bg-white p-5', panelClassName)}>
                {panelComponent ?? (
                  <DefaultReviewComponent navigateToSubstep={navigateToSubstep} data={data} />
                )}
              </DisclosurePanel>
            )}
          </>
        )}
      </Disclosure>

      <AccordionConfirmModals
        confirmationModalVisible={confirmationModalVisible}
        onCloseConfirmation={() => setConfirmationModalVisible(false)}
        onConfirmContinue={handleConfirmContinue}
        unconfirmationModalVisible={unconfirmationModalVisible}
        onCloseUnconfirmation={() => setUnconfirmationModalVisible(false)}
        onUnconfirmContinue={handleUnconfirmContinue}
        isConfirming={isConfirming}
      />
    </>
  );
};

export default Accordion;
